import { useCallback, useEffect, useState } from "react";
import { getMintSpaceById } from "../api/mintSpace";
import { getSubmissionsByMintSpace } from "../api/submissions";
import { MySubmissionRow, ReviewSubmissionRow, SubmissionDialogs, SubmissionFilters } from "../components/campaign/SubmissionRows";
import useSubmissionActions from "../components/campaign/useSubmissionActions";
import AppHeader from "../components/ds/AppHeader";
import EmptyState from "../components/ds/EmptyState";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";

/** Figma V6.4 for creators, O2's rows for whoever reviews the space's work. */
const MintSpaceSubmissionsPage = ({ isDark, setToast, mintSpaceId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [isMintSpaceOwner, setIsMintSpaceOwner] = useState(false);
  const [spaceName, setSpaceName] = useState("");
  const userId = user?._id || user?.id;

  const fetchSubmissions = useCallback(() => {
    if (!mintSpaceId) return;
    setLoading(true);
    getSubmissionsByMintSpace(mintSpaceId)
      .then((res) => {
        if (res.success) setSubmissions(res.data || []);
      })
      .catch(() => setToast?.(t("mintSpace.loadingSubmissions")))
      .finally(() => setLoading(false));
  }, [mintSpaceId, t, setToast]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  useEffect(() => {
    if (!mintSpaceId || !userId) {
      setIsMintSpaceOwner(false);
      return;
    }
    getMintSpaceById(mintSpaceId)
      .then((res) => {
        if (!res.success || !res.data) return;
        setSpaceName(res.data.name || "");
        const ownerId = res.data.owner?._id || res.data.owner;
        setIsMintSpaceOwner(res.data.isOwner ?? String(ownerId) === String(userId));
      })
      .catch(() => {});
  }, [mintSpaceId, userId]);

  const actions = useSubmissionActions({ setToast, refresh: fetchSubmissions, setSubmissions });

  const ownOnly = !isMintSpaceOwner && submissions.every((row) => String(row.submittedBy?._id || row.submittedBy) === String(userId));
  const shown = submissions.filter(
    (row) => (filter === "all" || row.status === filter) && (platform === "all" || row.platform === platform)
  );
  const approved = submissions.filter((row) => row.status === "approved");
  const views = approved.reduce((sum, row) => sum + (row.viewCount || 0), 0);
  const earned = approved.reduce((sum, row) => sum + (row.totalEarned || 0), 0);

  return (
    <div className="flex flex-col gap-space-24 font-ds">
      <AppHeader
        app="creator-rewards"
        eyebrow="Creator Rewards"
        title={ownOnly ? t("campaignFlow.yourSubmissionsIn", { space: spaceName }) : t("mintSpace.contentSubmissions")}
        subtitle={ownOnly ? "Creator Rewards" : t("mintSpace.reviewManageSubmissions")}
      />

      {ownOnly && (
        <div className="grid grid-cols-1 gap-space-12 min-[600px]:grid-cols-3 min-[600px]:gap-space-24">
          {[
            [t("campaignFlow.submitted"), submissions.length],
            [t("campaignFlow.approvedViews"), views.toLocaleString()],
            [t("campaignFlow.earned"), `${earned.toFixed(2)} ₾`],
          ].map(([label, value]) => (
            <div key={label} className="flex min-w-0 flex-col gap-space-4 rounded-[22px] bg-bg-inset p-space-16">
              <p className="truncate text-caps-label uppercase text-label-tertiary">{label}</p>
              <p className="truncate text-title-3 text-label-primary">{value}</p>
            </div>
          ))}
        </div>
      )}

      {submissions.length > 0 && (
        <SubmissionFilters submissions={submissions} status={filter} onStatus={setFilter} platform={platform} onPlatform={setPlatform} />
      )}

      {loading ? (
        <p className="py-space-40 text-center text-subheadline text-label-secondary">{t("mintSpace.loadingSubmissions")}</p>
      ) : shown.length === 0 ? (
        <div className="rounded-radius-lg bg-bg-raised">
          <EmptyState
            icon="upload"
            title={t(ownOnly ? "campaignFlow.noSubmissionsTitle" : "campaignFlow.noReviewTitle")}
            message={t(ownOnly ? "campaignFlow.noSubmissionsBody" : "mintSpace.submissionsWillAppear")}
          />
        </div>
      ) : (
        <ul className="flex flex-col divide-y divide-separator-default rounded-radius-lg bg-bg-raised py-space-8">
          {shown.map((submission) =>
            ownOnly ? (
              <MySubmissionRow key={submission._id} submission={submission} showCampaign tools actions={actions} />
            ) : (
              <ReviewSubmissionRow
                key={submission._id}
                submission={submission}
                canReview={isMintSpaceOwner}
                showCampaign
                ownId={userId}
                actions={actions}
              />
            )
          )}
        </ul>
      )}

      <SubmissionDialogs actions={actions} isDark={isDark} />
    </div>
  );
};

export default MintSpaceSubmissionsPage;
